import { CheckBadgeIcon } from '@heroicons/react/20/solid';

import type { PromoDiscountGrant } from './calculatePrice';

/**
 * Prominent order-page banner (#1706) telling a signed-in buyer that a
 * promotional DISCOUNT (#1689) or spendable promotional CREDIT (#1622) they hold
 * will be applied automatically at checkout.
 *
 * The reported problem: a buyer with a granted discount saw only the full catalog
 * price on the option cards and assumed the discount had not applied — the reduced
 * amount only surfaced in the final total after picking an option. This banner
 * states the concession up front, BEFORE any selection, so the buyer knows it is
 * live the moment they land on the page.
 *
 * Rendered only when there is actually something to announce. The discount handed
 * in is already scoped to the product in view (the server only returns it when it
 * applies), so a product-scoped discount never shows on a product it doesn't cover.
 * Display-only: the charged amount is recomputed server-side at checkout.
 */
export const PromoBanner: React.FC<{
  promoDiscount?: PromoDiscountGrant | null;
  promoCredit?: { total: number; expiresAt?: string | null } | null;
  /** Product name for context, e.g. "Your $650 off CFB discount". */
  productLabel?: string;
  locale?: string;
}> = ({ promoDiscount, promoCredit, productLabel, locale }) => {
  const fmtUSD0 = (n: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);

  // Last full day the grant is valid, in Pacific business time (it expires at the
  // instant after that day) — matching the "expires <date>" note in PriceInfo.
  const fmtExpires = (expiresAt?: string | null) =>
    expiresAt
      ? new Intl.DateTimeFormat(locale || 'en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'America/Los_Angeles',
        }).format(new Date(new Date(expiresAt).getTime() - 1000))
      : '';

  const discountLabel = promoDiscount
    ? promoDiscount.kind === 'pct'
      ? `${Math.round((promoDiscount.pct || 0) * 100)}% off`
      : `${fmtUSD0(Math.max(0, promoDiscount.amount || 0))} off`
    : '';
  const discountExpires = promoDiscount ? fmtExpires(promoDiscount.expiresAt) : '';

  const creditTotal = Math.max(0, promoCredit?.total || 0);
  const creditExpires = creditTotal > 0 ? fmtExpires(promoCredit?.expiresAt) : '';

  // Nothing to announce — render nothing so the page is unchanged for buyers
  // without a grant.
  if (!promoDiscount && creditTotal <= 0) {
    return null;
  }

  return (
    <div className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5 dark:border-emerald-400/30 dark:bg-emerald-400/10">
      <CheckBadgeIcon
        className="mt-0.5 size-6 shrink-0 text-emerald-600 dark:text-emerald-400"
        aria-hidden="true"
      />
      <div className="min-w-0">
        {promoDiscount && (
          <p className="font-semibold text-emerald-900 text-sm sm:text-base dark:text-emerald-100">
            Your {discountLabel}
            {productLabel ? ` ${productLabel}` : ''} discount is applied automatically at checkout.
          </p>
        )}
        {creditTotal > 0 && (
          <p className="font-semibold text-emerald-900 text-sm sm:text-base dark:text-emerald-100">
            You have {fmtUSD0(creditTotal)} in promotional credit, applied automatically at
            checkout.
          </p>
        )}
        <p className="mt-1 text-emerald-700 text-xs sm:text-sm dark:text-emerald-200/80">
          You'll see it reflected in your total once you choose an option below.
          {promoDiscount && discountExpires && ` Expires ${discountExpires}.`}
          {!promoDiscount && creditExpires && ` Expires ${creditExpires}.`}
        </p>
      </div>
    </div>
  );
};

export default PromoBanner;
